<template>
  <content-subpage
    class="add-from-library-content-subpage add-app"
    :visible="visible"
    :has-parent-subpage="hasParentSubpage"
    :titles="titles"
    :show-content-title="true"
    condensed
    header-sticky
    @content-subpage-hidden="actionHidden"
  >
    <template #main-content>
      <search-bar
        kind="white"
        active
        filter-type="selection"
        :keyword="searchPageParams.keyword"
        :placeholder="t('browse_or_search_resource_apps_placeholder')"
        @search="handleKeywordSearch"
      />
    </template>
    <template #main-content-bg>
      <template v-if="!hasKeyword">
        <card-list v-if="isLoading">
          <resource-search-card v-for="n in 1" :key="n" loading />
        </card-list>
        <empty-placeholder v-else-if="isEmptySearchResult">
          <template #icon>
            <generic-empty />
          </template>
          {{ emptyBrowseResultMessage }}
          <template v-if="emptyBrowseResultSubMessage" #sub>
            {{ emptyBrowseResultSubMessage }}
          </template>
        </empty-placeholder>
        <card-list v-else size="large">
          <resource-card
            v-for="item in searchResult.data"
            :key="item.uuid"
            v-bind="populateResourceCardProps(item)"
            v-on="populateResourceCardListeners(item)"
          />
        </card-list>
        <cv-pagination
          v-if="showPagination"
          :page="searchPageParams.pageNo"
          :page-sizes="[searchPageParams.pageSize]"
          :forwards-button-disabled="disableForwardsButton"
          :backwards-button-disabled="disableBackwardsButton"
          @change="handlePageChange"
        />
      </template>
      <div v-else class="search-result-wrapper">
        <card-list v-if="isLoading">
          <resource-search-card v-for="n in 20" :key="n" loading />
        </card-list>
        <template v-else>
          <div class="header">
            <span>{{ totalRecordLabel }}</span>
            <dropdown-component
              :label="t('search_result_sort_by_label')"
              :value="searchPageParams.sortBy"
              class="sort-by-filter"
              @change="handleSortByChange"
            >
              <cv-dropdown-item
                v-for="option in sortByOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.name }}
              </cv-dropdown-item>
            </dropdown-component>
          </div>
          <empty-placeholder v-if="isEmptySearchResult">
            <template #icon>
              <search-empty />
            </template>
            {{ emptySearchResultMessage }}
            <template #sub>
              {{ emptySearchResultSubMessage }}
            </template>
          </empty-placeholder>
          <card-list v-else size="">
            <resource-search-card
              v-for="item in searchResult.data"
              :key="item.uuid"
              v-bind="populateResourceSearchCardProps(item)"
              v-on="populateResourceCardListeners(item)"
            />
          </card-list>
          <cv-pagination
            v-if="showPagination"
            :page="searchPageParams.pageNo"
            :page-sizes="[searchPageParams.pageSize]"
            :forwards-button-disabled="disableForwardsButton"
            :backwards-button-disabled="disableBackwardsButton"
            @change="handlePageChange"
          />
        </template>
      </div>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from "vue";
import ResourceSearchCard from "@/components/ResourceSearchCard.vue";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";
import SearchBar from "@plugins/common/components/SearchBar.vue";
import CardList from "@plugins/card/components/CardList.vue";
import ContentSubpage from "@plugins/content-subpage/components/ContentSubpage.vue";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useI18n } from "vue-i18n";
import { ResourceCardProps } from "@/common/card-types.ts";
import { useAddFromLibrarySubpage } from "@modules/resource-search/composables/use-add-from-library-subpage.ts";
import {
  AddAppFromLibraryContentSubpageProps,
  AddAppFromLibraryContentSubpageSelectionEvent,
  AppLibraryResultItem,
  ResourceSearchParams,
} from "@modules/resource-search/resource-search-types.ts";
import {
  BROWSE_RESULT_PAGE_SIZE,
  RESOURCE_SEARCH_TYPE,
} from "@modules/resource-search/resource-search-helper.ts";
import DropdownComponent from "@plugins/common/components/DropdownComponent.vue";
import ResourceCard from "@/components/ResourceCard.vue";
import { useAppCard } from "@modules/resource-browse/composables/use-app-card.ts";
import { useAppSearchCard } from "@modules/resource-search/composables/use-app-search-card.ts";
import GenericEmpty from "@images/placeholder/empty-states/GenericEmpty.svg";
import SearchEmpty from "@images/placeholder/empty-states/SearchEmpty.svg";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import AppDetailContentSubpage from "@modules/app-list/components/AppDetailContentSubpage.vue";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { isDefined } from "@utils/var.check";

// Props
interface Props {
  params: AddAppFromLibraryContentSubpageProps;
}

const { params: props } = defineProps<Props>();

const emit = defineEmits<{
  selectionSaved: [data: AddAppFromLibraryContentSubpageSelectionEvent];
}>();

// Composables
const { t } = useI18n();

// State
const searchPageParams = ref<ResourceSearchParams>({
  keyword: "",
  location: [],
  pageNo: 1,
  pageSize: BROWSE_RESULT_PAGE_SIZE,
  resource: RESOURCE_SEARCH_TYPE.APP,
});

if (isDefined(props.gradable)) {
  searchPageParams.value.gradable = props.gradable;
}

const triggerSearch = async () => {
  isLoading.value = true;
  await fetchSearchData();
  isLoading.value = false;
};

const triggerSearchAndAggregateFilters = async () => {
  isLoading.value = true;
  await fetchSearchData();
  isLoading.value = false;
};

const {
  disableForwardsButton,
  disableBackwardsButton,
  emptyBrowseResultMessage,
  emptyBrowseResultSubMessage,
  emptySearchResultMessage,
  emptySearchResultSubMessage,
  hasKeyword,
  isLoading,
  isEmptySearchResult,
  searchResult,
  sortByOptions,
  showPagination,
  totalRecordLabel,
  fetchSearchData,
  handlePageChange,
  handleKeywordSearch,
  handleSortByChange,
} = useAddFromLibrarySubpage(
  searchPageParams,
  props,
  { fromResourceLibrary: props.fromResourceLibrary },
  triggerSearch,
  triggerSearchAndAggregateFilters,
);

const contentSubpageInstance = useContentSubpageInstance();
const { populateAppCardPropsFromLibrary } = useAppCard();
const { populateAppSearchCardPropsFromLibrary } = useAppSearchCard();

// Composable
const { passiveApiFailHandler } = useErrorHandler();

// Content Subpage setup
const { hasParentSubpage, titles, actionHidden, visible, defaultExpose } = useContentSubpage({
  baseTitle: props.baseTitle,
});

// Methods
const getSelectionEvent = (
  item: AppLibraryResultItem,
): AddAppFromLibraryContentSubpageSelectionEvent => {
  return {
    appUuid: item.uuid,
    onSuccess: () => {
      actionHidden();
    },
    onFailure: () => {
      passiveApiFailHandler();
    },
  };
};

const populateResourceCardProps = (item: AppLibraryResultItem): ResourceCardProps => {
  return populateAppCardPropsFromLibrary(item);
};

const populateResourceCardListeners = (
  item: AppLibraryResultItem,
): { click: () => void; embed: () => void } => {
  return {
    click: () => {
      handleViewDetails(item, true, t("embed_app_browse_apps_title"));
    },
    embed: () => {
      emit("selectionSaved", getSelectionEvent(item));
    },
  };
};

const populateResourceSearchCardProps = (item: AppLibraryResultItem) => {
  return populateAppSearchCardPropsFromLibrary(item);
};

const handleViewDetails = (
  resourceData: AppLibraryResultItem,
  hasParentSubpage = false,
  customBackLabel: string | null = null,
): void => {
  contentSubpageInstance.show(
    AppDetailContentSubpage,
    {
      customBackLabel: customBackLabel,
      hasParentSubpage: hasParentSubpage,
      highlights: resourceData.highlights,
      uuid: resourceData?.uuid,
    },
    {
      onAppSelected: () => {
        emit("selectionSaved", getSelectionEvent(resourceData));
      },
    },
  );
};

defineExpose(defaultExpose);

// Lifecycle hooks
onBeforeMount(async () => {
  isLoading.value = true;
  await fetchSearchData();
  isLoading.value = false;
});

titles.value.push(t("embed_app_browse_apps_title"));
</script>
